import { QueryClient, focusManager } from "@tanstack/react-query";
import * as Sentry from '@sentry/react';

// ─── Configs reutilizáveis por categoria de dados ───
// Toda query deve usar uma dessas configs (via spread ou default global).
// Hooks de CRM operacional não precisam de config explícita (cobertos pelo default).
// Hooks de analytics, structural, session e preferences DEVEM usar config explícita.
export const queryConfigs = {
  // A — CRM Operacional (DEFAULT): dados que múltiplos reps editam simultaneamente
  // Leads, deals, activities, companies, segments, follow-up queue, notifications
  crm: {
    staleTime: 30 * 1000,           // 30 segundos
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    retry: 2,
  },

  // B — Chat/Realtime: dados atualizados via Supabase Realtime quando em /chat
  // Conversations, messages, notes, tags de chat
  realtime: {
    staleTime: 0,
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    retry: 1,
  },

  // C — Analytics/Dashboard: queries pesadas com agregações
  // KPIs, funnel, trends, channel performance, rep performance, revenue, forecast
  analytics: {
    staleTime: 10 * 60 * 1000,      // 10 minutos
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    retry: 2,
  },

  // D — Dados Estruturais: configurados por admin, mudam raramente durante o dia
  // Pipelines, tags, products, loss reasons, qualifying questions, instâncias,
  // sequences, workflows, vox assistants/voices, custom fields, agents
  structural: {
    staleTime: 30 * 60 * 1000,      // 30 minutos
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 2,
  },

  // E — Dados de Sessão: não mudam durante a sessão do usuário
  // currentUser, currentOrgId, isAdmin, notification preferences
  session: {
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 1,
  },

  // F — Preferências de UI: dados do usuário que não precisam de frescor
  // Column widths, filtros salvos, persistent filters, database filter state
  preferences: {
    staleTime: 5 * 60 * 1000,       // 5 minutos
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 1,
  },
} as const;

// ─── QueryClient com default CRM (o mais seguro) ───
// Default = Categoria A (CRM). Assim hooks novos ou não categorizados
// mostram dados frescos. É melhor um hook de analytics refetchar demais
// (custo de performance) do que um hook de leads mostrar dados stale (custo operacional).
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000,           // 30 segundos
      gcTime: 24 * 60 * 60 * 1000,    // 24 horas (stale-while-revalidate)
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
      refetchInterval: false,
      retry: 2,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    },
    mutations: {
      retry: 1,
      onError: (error: Error) => {
        Sentry.captureException(error, {
          tags: { source: 'react-query-mutation' },
        });
      },
    },
  },
});

// ─── Captura de erros de queries no Sentry ───
queryClient.getQueryCache().config.onError = (error: Error, query) => {
  // Não capturar erros de rede (retry vai resolver)
  if (error instanceof TypeError && error.message === 'Failed to fetch') return;
  Sentry.captureException(error, {
    tags: {
      source: 'react-query',
      queryKey: JSON.stringify(query.queryKey).slice(0, 200),
    },
  });
};

// ─── Debounce focus refetch ───
// Agrupa todos os refetches em um burst 1s após focus.
// Evita disparar muitas queries simultâneas em cada alt-tab.
// Se o rep faz alt-tab rápido (< 1s), nenhum refetch é disparado.
focusManager.setEventListener((handleFocus) => {
  let timeout: ReturnType<typeof setTimeout>;

  const onFocus = () => {
    clearTimeout(timeout);
    timeout = setTimeout(() => handleFocus(true), 1000);
  };

  const onBlur = () => {
    clearTimeout(timeout);
    handleFocus(false);
  };

  window.addEventListener('focus', onFocus);
  window.addEventListener('blur', onBlur);

  return () => {
    window.removeEventListener('focus', onFocus);
    window.removeEventListener('blur', onBlur);
    clearTimeout(timeout);
  };
});

// ─── Debug interceptors (apenas em DEV) ───
if (import.meta.env.DEV) {
  const formatQueryKey = (key: unknown) => {
    if (key === undefined) return "ALL";
    try {
      return JSON.stringify(key);
    } catch {
      return String(key);
    }
  };

  const originalInvalidateQueries = queryClient.invalidateQueries.bind(queryClient);
  queryClient.invalidateQueries = function(options: any) {
    console.log('[queryClient] invalidateQueries:', {
      queryKey: formatQueryKey(options?.queryKey),
      timestamp: new Date().toISOString(),
    });
    return originalInvalidateQueries(options);
  };

  const originalRefetchQueries = queryClient.refetchQueries.bind(queryClient);
  queryClient.refetchQueries = function(options: any) {
    console.log('[queryClient] refetchQueries:', {
      queryKey: formatQueryKey(options?.queryKey),
      timestamp: new Date().toISOString(),
    });
    return originalRefetchQueries(options);
  };
}
